/* src/pages/Donation/StepPages/SplitDonation/index.jsx */

import React, { useState, useEffect } from "react";
import "./index.scss";
import moment from "moment-timezone";
import { useSelector } from "react-redux";
import CustomLayout from "../../../../components/CustomLayout";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomCard from "../../../../components/CustomCard";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  containerVariants,
  cardVariants,
  optionsVariants,
  donationAmountVariants
} from "../../../../assets/framer/styles";
import { calculateTotalAmount, formatDate } from "../../../../utils/helpers";
import { getRamadanConfig } from "../../../../api/payments";
import {
  DHUL_HIJJAH_2026_ARAFAH_DATE,
  DHUL_HIJJAH_2026_EID_DATE,
  getCanonicalTemplateKey,
  isDhulHijjahTemplateKey,
  isFridayTemplateKey,
  isRamadanTemplateKey,
  isRamadanTenNightTemplateKey
} from "../../../../utils/templateUtils";

// 1) Import i18n
import { useTranslation } from "react-i18next";

const SplitDonation = () => {
  // 2) Use the t function
  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedOption, setSelectedOption] = useState(
    parseInt(searchParams.get("scheduleOption") || "0", 10)
  );
  const timezone = useSelector((state) => state.donation.timezone);
  const startDate =
    useSelector((state) => state.donation.startDate) || new Date();
  const endDate = useSelector((state) => state.donation.endDate);
  const hourOfDonation =
    useSelector((state) => state.donation.hourOfDonation) || 22;
  const interval = useSelector((state) => state.donation.interval);
  const [customAmounts, setCustomAmounts] = useState(Array(10).fill(0));
  const breakdown = parseBreakdown(searchParams.get("breakdown"));
  const userTimezoneName = timezone || "UTC";
  const template = searchParams.get("template") || "";
  const appealCode =
    searchParams.get("appealCode") || searchParams.get("appeal") || "";
  const templateKey = getCanonicalTemplateKey(template);
  const isLast10NightsAppeal = appealCode.toLowerCase() === "last10nights";
  const isFridayTemplate = isFridayTemplateKey(templateKey);
  const isRamadanTemplate = isRamadanTemplateKey(templateKey);
  const isDhulHijjahTemplate = isDhulHijjahTemplateKey(templateKey);
  const donationAmount = calculateTotalAmount(breakdown);
  const [ramadanConfig, setRamadanConfig] = useState({ pendingEnabled: true, startDate: null });
  const isRamadanFlow = isRamadanTemplateKey(templateKey);

  useEffect(() => {
    if (isRamadanFlow) {
      getRamadanConfig()
        .then((c) => setRamadanConfig({ pendingEnabled: c?.pendingEnabled ?? true, startDate: c?.startDate || null }))
        .catch(() => setRamadanConfig({ pendingEnabled: true, startDate: null }));
    }
  }, [isRamadanFlow, templateKey]);

  // Generate donation schedule with the extra "template" parameter
  const donationScheduleBreakdown = generateDonationSchedule(
    startDate,
    endDate,
    hourOfDonation,
    interval,
    donationAmount,
    selectedOption,
    userTimezoneName,
    template,
    appealCode
  );

  // Use the locked Ramadan baseline for display when presets collapse to last 10/27th
  const displayStartDate =
    isRamadanFlow && (selectedOption === 2 || selectedOption === 3)
      ? "2026-02-17"
      : startDate;

  useEffect(() => {
    if (selectedOption === 1) {
      // "Custom" case: Reset custom amounts array
      setCustomAmounts(Array(donationScheduleBreakdown.length).fill(0));
    }
  }, [selectedOption, donationScheduleBreakdown.length]);

  const handleNext = () => {
    if (selectedOption === 1) {
      // if "Custom", use customAmounts
      const updatedSchedule = donationScheduleBreakdown.map((day, i) => ({
        ...day,
        amount: customAmounts[i]
      }));
      updateQueryParams(
        setSearchParams,
        searchParams,
        updatedSchedule,
        selectedOption
      );
    } else {
      updateQueryParams(
        setSearchParams,
        searchParams,
        donationScheduleBreakdown,
        selectedOption
      );
    }
  };

  const currentTotal = customAmounts.reduce((sum, val) => sum + val, 0);

  return (
    <CustomLayout
      // 3) Localize the main title & description
      title={t("splitDonationTitle")}
      description={
        isFridayTemplate
          ? t("splitDonationFridayDescription")
          : isLast10NightsAppeal
          ? t("splitDonationDescriptionLast10Nights")
          : isDhulHijjahTemplate
          ? t("splitDonationDescriptionDhulHijjah")
          : t("splitDonationDescription")
      }
      button={
        <CustomButton
          // 4) Localize the button text
          text={t("splitDonationButtonNext")}
          onClick={handleNext}
          disabled={selectedOption === 1 && currentTotal !== donationAmount}
        />
      }
    >
      {!isFridayTemplate && (
        <DonationTimeOptions
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          template={template}
          isLast10NightsAppeal={isLast10NightsAppeal}
        />
      )}
      <DonationAmount
        donationAmount={donationAmount}
        currentTotal={
          isFridayTemplate
            ? donationAmount
            : selectedOption === 1
            ? currentTotal
            : donationAmount
        }
        isCustom={isFridayTemplate ? 0 : selectedOption === 1}
        isFridayTemplate={isFridayTemplate}
      />
      {(isRamadanTemplate || ((selectedOption === 0 || selectedOption === 2 || selectedOption === 3) && !isFridayTemplate)) && (
        <div
          style={{
            marginTop: 8,
            marginBottom: 8,
            padding: "10px 12px",
            background: "#f1f4fa",
            border: "1px solid #d8deea",
            borderRadius: 10,
            color: "#0a2a63",
            fontSize: 13,
            lineHeight: 1.4
          }}
        >
          {isRamadanTemplate && t("splitDonationChargesNote")}
          {isRamadanTemplate && selectedOption !== 1 && " "}
          {isRamadanTemplate && selectedOption !== 1 && t("splitDonationRoundingNote")}
          {!isRamadanTemplate && (selectedOption === 0 || selectedOption === 2 || selectedOption === 3) && t("splitDonationRoundingNote")}
        </div>
      )}
      <DonationSchedule
        startDate={displayStartDate}
        donationScheduleBreakdown={donationScheduleBreakdown}
        isCustom={selectedOption === 1}
        customAmounts={customAmounts}
        setCustomAmounts={setCustomAmounts}
        donationAmount={donationAmount}
        currentTotal={currentTotal}
        isFridayTemplate={isFridayTemplate}
        ramadanStartDate={ramadanConfig.startDate}
        isRamadanTemplate={isRamadanTemplate}
        template={template}
      />
    </CustomLayout>
  );
};

// Helper: Parse breakdown from URL query parameter
const parseBreakdown = (breakdownParam) => {
  if (!breakdownParam) return [];
  try {
    return JSON.parse(decodeURIComponent(breakdownParam));
  } catch {
    return [];
  }
};

// Generate donation schedule with different logic based on calendar type
export const generateDonationSchedule = (
  startDate,
  endDate,
  hourOfDonation,
  interval,
  donationAmount,
  selectedOption,
  userTimezoneName,
  template,
  appealCode = ""
) => {
  const templateKey = getCanonicalTemplateKey(template);
  const isLast10NightsAppeal =
    (appealCode || "").toLowerCase() === "last10nights";
  // Determine if we are in a 10-night calendar (Hijri10/Lunar10 + Ramadan 10 aliases)
  const isTenNightCalendar = isRamadanTenNightTemplateKey(templateKey);
  const isRamadanCalendar = isRamadanTemplateKey(templateKey);
  const now = moment().tz(userTimezoneName).startOf("day");
  // For Ramadan presets (last 10, 27th), lock to full Ramadan range
  const shouldLockRamadanRange = isRamadanCalendar && (selectedOption === 2 || selectedOption === 3);
  const startForCalc = shouldLockRamadanRange ? "2026-02-17" : startDate;
  const endForCalc = shouldLockRamadanRange ? "2026-03-18" : endDate;
  const startDateLocal = moment.tz(startForCalc, "YYYY-MM-DD", userTimezoneName);
  // Build full schedule without trimming past dates so preset options stay anchored
  let fullSchedule = [];
  if (isRamadanCalendar && !isTenNightCalendar) {
    // Force exactly 30 nights for Ramadan calendars (per confirmed start/end)
    const base = startDateLocal
      .clone()
      .hour(hourOfDonation)
      .minute(0)
      .second(0);
    fullSchedule = Array.from({ length: 30 }, (_, i) => ({
      date: base.clone().add(i, "days").utc().toISOString(),
      amount: 0
    }));
    if (isLast10NightsAppeal) {
      fullSchedule = fullSchedule.slice(-10);
    }
  } else {
    const currentMoment = startDateLocal
      .clone()
      .hour(hourOfDonation)
      .minute(0)
      .second(0);
    const endMoment = moment
      .tz(endForCalc, "YYYY-MM-DD", userTimezoneName)
      .hour(hourOfDonation)
      .minute(0)
      .second(0);

    while (currentMoment.isSameOrBefore(endMoment)) {
      fullSchedule.push({
        date: currentMoment.utc().toISOString(),
        amount: 0
      });
      currentMoment.add(interval, "days");
    }
  }

  // Work on a copy for allocation
  let schedule = fullSchedule.map((d) => ({ ...d }));

  const isDhulHijjah = isDhulHijjahTemplateKey(templateKey);
  const isFridayTemplate = isFridayTemplateKey(templateKey);

  // For "equally" options, divide only across future nights (past dates are filtered out later)
  const futureNightCount = schedule.filter((day) => !moment(day.date).isBefore(now, "day")).length || 1;

  // Penny allocation helper: distributes amount across future nights so total equals donationAmount exactly
  const assignEqualAmountsWithPennyAllocation = () => {
    const totalCents = Math.round(donationAmount * 100);
    const baseCents = Math.floor(totalCents / futureNightCount);
    const remainder = totalCents - baseCents * futureNightCount;
    let nightsAssigned = 0;
    schedule.forEach((day) => {
      if (moment(day.date).isBefore(now, "day")) {
        day.amount = 0;
      } else {
        const extraCent = nightsAssigned < remainder ? 1 : 0;
        day.amount = parseFloat(((baseCents + extraCent) / 100).toFixed(2));
        nightsAssigned++;
      }
    });
  };

  // Penny allocation for a subset of nights (e.g. last 10, odd nights)
  const assignPennyAllocationToNights = (targetNights) => {
    const futureTargets = targetNights.filter((d) => !moment(d.date).isBefore(now, "day"));
    const count = futureTargets.length || 1;
    const totalCents = Math.round(donationAmount * 100);
    const baseCents = Math.floor(totalCents / count);
    const remainder = totalCents - baseCents * count;
    let nightsAssigned = 0;
    targetNights.forEach((day) => {
      if (moment(day.date).isBefore(now, "day")) {
        day.amount = 0;
      } else {
        const extraCent = nightsAssigned < remainder ? 1 : 0;
        day.amount = parseFloat(((baseCents + extraCent) / 100).toFixed(2));
        nightsAssigned++;
      }
    });
  };

  if (isTenNightCalendar) {
    // For Hijri10/Lunar10 calendars
    switch (selectedOption) {
      case 0: // Equally
        assignEqualAmountsWithPennyAllocation();
        break;
      case 1: // Custom
        schedule.forEach((day) => {
          day.amount = 0;
        });
        break;
      case 2: // 27th Night Only: use startDate + 6 days
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = startDateLocal
            .clone()
            .add(6, "days")
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      case 3: // Day of Arafah
        {
          const oddNights = schedule.filter((day, i) => (i + 1) % 2 === 1);
          oddNights.forEach((day) => {
            day.amount = parseFloat(
              (donationAmount / oddNights.length).toFixed(2)
            );
          });
          schedule.forEach((day, i) => {
            if ((i + 1) % 2 === 0) day.amount = 0;
          });
        }
        break;
      case 4: // Eid Al Adha
        {
          const oddCount = schedule.filter(
            (day, i) => (i + 1) % 2 === 1
          ).length;
          const evenCount = schedule.filter(
            (day, i) => (i + 1) % 2 === 0
          ).length;
          schedule.forEach((day, i) => {
            if ((i + 1) % 2 === 1) {
              day.amount = parseFloat(
                ((donationAmount * (2 / 3)) / oddCount).toFixed(2)
              );
            } else {
              day.amount = parseFloat(
                ((donationAmount * (1 / 3)) / evenCount).toFixed(2)
              );
            }
          });
        }
        break;
      default:
        break;
    }
  } else if (isDhulHijjah) {
    switch (selectedOption) {
      case 0: // Equally
        assignEqualAmountsWithPennyAllocation();
        break;
      case 1: // Custom
        schedule.forEach((day) => {
          day.amount = 0;
        });
        break;
      case 2: // Day of Arafah
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = moment
            .tz(DHUL_HIJJAH_2026_ARAFAH_DATE, "YYYY-MM-DD", userTimezoneName)
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      case 3: // Eid al-Adha
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = moment
            .tz(DHUL_HIJJAH_2026_EID_DATE, "YYYY-MM-DD", userTimezoneName)
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      case 4: // More on Odd Nights – 2/3 to odd nights, 1/3 to even nights
        {
          const oddCount = schedule.filter(
            (day, i) => (i + 1) % 2 === 1
          ).length;
          const evenCount = schedule.filter(
            (day, i) => (i + 1) % 2 === 0
          ).length;
          schedule.forEach((day, i) => {
            if ((i + 1) % 2 === 1) {
              day.amount = parseFloat(
                ((donationAmount * (2 / 3)) / oddCount).toFixed(2)
              );
            } else {
              day.amount = parseFloat(
                ((donationAmount * (1 / 3)) / evenCount).toFixed(2)
              );
            }
          });
        }
        break;
      default:
        break;
    }
  } else if (isRamadanCalendar && !isTenNightCalendar) {
    // Ramadan 30-night calendars
    switch (selectedOption) {
      case 0: // Equally across all nights
        assignEqualAmountsWithPennyAllocation();
        break;
      case 1: // Custom (clear amounts; handled upstream)
        schedule.forEach((day) => {
          day.amount = 0;
        });
        break;
      case 2: // Last 10 nights only (nights 21–30)
        if (schedule.length >= 10) {
          const lastTen = schedule.slice(-10);
          schedule.forEach((day, i) => {
            if (i < schedule.length - 10) day.amount = 0;
          });
          assignPennyAllocationToNights(lastTen);
        }
        break;
      case 3: // Odd nights only (within last 10)
        if (schedule.length >= 10) {
          const lastTen = schedule.slice(-10);
          const oddLastTen = lastTen.filter((_, idx) => (idx + 1) % 2 === 1); // positions 1,3,5,7,9 within last ten
          schedule.forEach((day) => {
            day.amount = 0;
          });
          assignPennyAllocationToNights(oddLastTen);
        } else {
          // fallback: split across available odd nights
          const oddNights = schedule.filter((_, i) => (i + 1) % 2 === 1);
          schedule.forEach((day) => {
            day.amount = 0;
          });
          assignPennyAllocationToNights(oddNights);
        }
        break;
      case 4: // 27th night only (night index 27 → offset 26)
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = startDateLocal
            .clone()
            .add(26, "days")
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      default:
        break;
    }
  } else {
    // For regular calendars (including Fridays fallback)
    switch (selectedOption) {
      case 0: // Equally
        if (isFridayTemplate) {
          schedule.forEach((day) => {
            day.amount = parseFloat(donationAmount.toFixed(2));
          });
        } else {
          assignEqualAmountsWithPennyAllocation();
        }
        break;
      case 1: // Custom
        schedule.forEach((day) => {
          day.amount = 0;
        });
        break;
      case 2: // Last 10 Nights
        if (schedule.length >= 10) {
          const lastTen = schedule.slice(-10);
          schedule.forEach((day, i) => {
            if (i < schedule.length - 10) day.amount = 0;
          });
          assignPennyAllocationToNights(lastTen);
        } else {
          assignEqualAmountsWithPennyAllocation();
        }
        break;
      case 3: // 27th Night Only: use startDate + 26 days
        schedule.forEach((day) => {
          day.amount = 0;
        });
        {
          const targetDay = startDateLocal
            .clone()
            .add(26, "days")
            .startOf("day");
          const dayIndex = schedule.findIndex((d) =>
            moment(d.date).isSame(targetDay, "day")
          );
          if (dayIndex !== -1) {
            schedule[dayIndex].amount = parseFloat(donationAmount.toFixed(2));
          }
        }
        break;
      default:
        break;
    }
  }

  // After allocation, drop past dates so we don't schedule backdated charges
  return schedule.filter((day) => !moment(day.date).isBefore(now, "day"));
};

// Update URL query parameters with the generated schedule and selected option
const updateQueryParams = (
  setSearchParams,
  searchParams,
  donationScheduleBreakdown,
  selectedOption
) => {
  const newParams = new URLSearchParams(searchParams);
  newParams.set("stage", "3");
  newParams.set(
    "schedule",
    encodeURIComponent(JSON.stringify(donationScheduleBreakdown))
  );
  newParams.set("scheduleOption", selectedOption);
  setSearchParams(newParams);
};

/* --------------------------------------------------------------------------------
   DonationTimeOptions (checkbox group)
--------------------------------------------------------------------------------- */
const DonationTimeOptions = ({
  selectedOption,
  setSelectedOption,
  template,
  isLast10NightsAppeal
}) => {
  const { t } = useTranslation();

  const templateKey = getCanonicalTemplateKey(template);
  const isTenNightCalendar = isRamadanTenNightTemplateKey(templateKey);
  const isRamadanCalendar = isRamadanTemplateKey(templateKey);
  const isDhulHijjahCalendar = isDhulHijjahTemplateKey(templateKey);
  const isRamadanNonTen = isRamadanCalendar && !isTenNightCalendar;

  // Keep explicit option values so UI labels can vary without breaking schedule logic.
  let donationOptions;
  if (isTenNightCalendar) {
    donationOptions = [
      { value: 0, label: t("splitDonationOptionEqually") },
      { value: 1, label: t("splitDonationOptionCustom") },
      { value: 2, label: t("splitDonationOption27thNight") },
      { value: 3, label: t("splitDonationOptionOddNights") },
      { value: 4, label: t("splitDonationOptionMoreOnOddNights") }
    ];
  } else if (isRamadanNonTen) {
    donationOptions = isLast10NightsAppeal
      ? [
          { value: 0, label: "Equally across all nights" },
          { value: 1, label: "Custom amounts" },
          { value: 3, label: "Odd nights only" },
          { value: 4, label: "27th night only" }
        ]
      : [
          { value: 0, label: "Equally across all nights" },
          { value: 1, label: "Custom amounts" },
          { value: 2, label: "Last 10 nights only" },
          { value: 3, label: "Odd nights only" },
          { value: 4, label: "27th night only" }
        ];
  } else if (isDhulHijjahCalendar) {
    donationOptions = [
      { value: 0, label: t("splitDonationOptionEqually") },
      { value: 1, label: t("splitDonationOptionCustom") },
      { value: 2, label: t("splitDonationArafah") },
      { value: 3, label: t("splitDonationEidDay") }
    ];
  } else {
    // Fallback if no recognized template
    donationOptions = [
      { value: 0, label: t("splitDonationOptionEqually") },
      { value: 1, label: t("splitDonationOptionCustom") }
    ];
  }

  return (
    <motion.div
      className="donation-time-options-container"
      variants={optionsVariants}
      initial="hidden"
      animate="visible"
    >
      {donationOptions.map((option) => (
        <CustomCheckbox
          key={option.value}
          index={option.value}
          option={option.label}
          isSelected={option.value === selectedOption}
          onSelect={() => setSelectedOption(option.value)}
        />
      ))}
    </motion.div>
  );
};

/* --------------------------------------------------------------------------------
   DonationAmount (displays donation totals)
--------------------------------------------------------------------------------- */
const DonationAmount = ({
  donationAmount,
  currentTotal,
  isCustom,
  isFridayTemplate
}) => {
  const { t } = useTranslation();

  return (
    <motion.div
      className="donation-amount-container"
      variants={donationAmountVariants}
      initial="hidden"
      animate="visible"
    >
      {/* 5) Localize "Your Donation Amount" */}
      <span className="donation-amount-label">
        {isFridayTemplate
          ? t("splitDonationDonationFridayAmountLabel")
          : t("splitDonationDonationAmountLabel")}
      </span>
      <span className="donation-amount-value">
        {isCustom ? (
          <>
            <span
              style={{
                color:
                  isCustom && currentTotal !== donationAmount
                    ? "#e80909"
                    : "inherit"
              }}
            >
              ${currentTotal.toFixed(2)}
            </span>{" "}
            / ${donationAmount.toFixed(2)}
          </>
        ) : (
          `$${donationAmount.toFixed(2)}`
        )}
      </span>
    </motion.div>
  );
};

/* --------------------------------------------------------------------------------
   DonationSchedule (renders day cards)
--------------------------------------------------------------------------------- */
const DonationSchedule = ({
  startDate,
  donationScheduleBreakdown,
  isCustom,
  customAmounts,
  setCustomAmounts,
  donationAmount,
  currentTotal,
  isFridayTemplate,
  ramadanStartDate,
  isRamadanTemplate,
  template
}) => (
  <motion.div
    className="four-col-responsive-list-grid"
    variants={containerVariants}
    initial="hidden"
    animate="visible"
  >
    {isCustom
      ? donationScheduleBreakdown.map((day, index) => {
          const dayNumber =
            moment(day.date)
              .startOf("day")
              .diff(moment(startDate).startOf("day"), "days") + 1;
          const displayDate = formatDate(day.date);
          return (
            <motion.div key={index} variants={cardVariants}>
              <CustomCard
                type="scheduleDay"
                dayNumber={dayNumber}
                date={displayDate}
                amount={customAmounts[index]}
                disabled={moment(day.date).isBefore(moment().startOf("day"))}
                onAmountChange={(newAmount) => {
                  const newArr = [...customAmounts];
                  newArr[index] = newAmount;
                  setCustomAmounts(newArr);
                }}
                donationAmount={donationAmount}
                currentTotal={currentTotal}
                template={template}
              />
            </motion.div>
          );
        })
      : donationScheduleBreakdown
          .filter((day, index) => {
            const amount = day.amount;
            return Number(amount) > 0;
          })
          .map((day, index) => {
            const dayNumber =
              moment(day.date)
                .startOf("day")
                .diff(moment(startDate).startOf("day"), "days") + 1;

            const isFirstFriday = moment(day.date).isSame(
              moment(day.date).startOf("month").day(5),
              "day"
            );
            const displayDate = formatDate(day.date);

            return (
              <motion.div key={index} variants={cardVariants}>
                <CustomCard
                  type="scheduleDay"
                  dayNumber={
                    isFridayTemplate ? moment(day.date).format("DD") : dayNumber
                  }
                  date={displayDate}
                  amount={day.amount}
                  disabled={true}
                  donationAmount={donationAmount}
                  isFirstFriday={isFirstFriday}
                  currentTotal={currentTotal}
                  template={template}
                />
              </motion.div>
            );
          })}
  </motion.div>
);

export default SplitDonation;
